/**
 * @fileoverview Click & Collect Orders API functions
 * 
 * Handles Click & Collect order management including:
 * - Get all C&C orders
 * - Get single C&C order by ID
 * - Update C&C order status
 * 
 * @module api/click-collect
 */

import apiClient, { 
  handleUnauthorizedResponse, 
  getAuthHeaders, 
  getRestaurantId 
} from './client';

// ============================================================================
// CLICK & COLLECT ORDER QUERIES
// ============================================================================

/**
 * Get all Click & Collect orders
 * @param {Object} router - Next.js router
 * @param {Object} options - Query options
 * @param {string} options.status - Status filter (NOT_PAID, IN_PROGRESS, PAID, CANCELLED)
 * @param {number} options.limit - Limit results
 * @param {number} options.offset - Offset for pagination
 * @returns {Promise} Click & Collect orders list
 */
export const getClickCollectOrders = async (router, options = {}) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/click-and-collect/orders`, {
      headers: getAuthHeaders(),
      params: {
        status_filter: options.status,
        limit: options.limit || 50,
        offset: options.offset || 0,
      },
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Get a specific Click & Collect order by ID
 * @param {Object} router - Next.js router
 * @param {number} orderId - Order ID
 * @returns {Promise} Click & Collect order details
 */
export const getClickCollectOrderById = async (router, orderId) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.get(`/admin/restaurant/${restaurantId}/click-and-collect/orders/${orderId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Update Click & Collect order status
 * @param {Object} router - Next.js router
 * @param {number} orderId - Order ID
 * @param {string} status - New status
 * @returns {Promise} Updated order
 */
export const updateClickCollectOrderStatus = async (router, orderId, status) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/click-and-collect/orders/${orderId}/status`,
      { status },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

/**
 * Cancel a Click & Collect order
 * @param {Object} router - Next.js router
 * @param {number} orderId - Order ID
 * @param {string} reason - Cancellation reason
 * @returns {Promise} Result
 */
export const cancelClickCollectOrder = async (router, orderId, reason) => {
  const restaurantId = getRestaurantId();
  try {
    const response = await apiClient.patch(
      `/admin/restaurant/${restaurantId}/click-and-collect/orders/${orderId}/cancel`,
      { reason },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleUnauthorizedResponse(router);
    }
    throw error;
  }
};

